/**
 * RFC 7807 problem+json (CLAUDE.md §9). `title` NIJE rečenica nego i18n ključ —
 * prijevod je na klijentu, pa se ovdje ništa ne prikazuje sirovo.
 *
 * Validacijske greške (422) dolaze kao `errors: { Svojstvo: ["kod", …] }`, gdje
 * je svojstvo PascalCase ime s poslužitelja. Preslikavamo ga u snake_case jer je
 * to ime polja u obrascu i u ugovoru — tako poruka sjeda uz polje na koje se
 * odnosi, a ne u zajednički okvir na vrhu.
 */

export interface ProblemDocument {
  type?: string
  title?: string
  status?: number
  detail?: string
  errors?: Record<string, string[]>
  /** Ekstenzije (RFC 7807 §3.2): npr. `vehicle_kept` uz `booking_already_taken`. */
  [dodatno: string]: unknown
}

/** Standardni članovi; sve ostalo je ekstenzija i nosi značenje uz ključ. */
const STANDARDNI = ['type', 'title', 'status', 'detail', 'instance', 'errors']

/** Ključ koji koristimo kad poslužitelj nije rekao ništa upotrebljivo. */
export const NEPOZNATA_GRESKA = 'unknown_error'

/** Mreža je pukla prije nego što je odgovor uopće stigao. */
export const MREZNA_GRESKA = 'network_error'

/**
 * Odgovor nije stigao u zadanom roku. Nosi vlastiti ključ, a ne `network_error`:
 * veza koja tiho stoji i veza koja je pukla izgledaju korisniku isto samo dok mu
 * se ne kaže da pokuša ponovno.
 */
export const ISTEK_ZAHTJEVA = 'request_timeout'

export class ApiError extends Error {
  readonly status: number

  /** i18n ključ iz `title` — ono što se prikazuje korisniku. */
  readonly key: string

  /** Polje (snake_case) → i18n ključevi pravila koja su pala. */
  readonly fields: Readonly<Record<string, string[]>>

  /**
   * Ekstenzije problem+json dokumenta. Ključ greške kaže ŠTO je palo, a ovo što
   * je iza sebe ostavilo — `vehicle_kept` uz izgubljeni nalog znači da je vozilo
   * ipak preuzeto i da ga vozač zadržava.
   */
  readonly dodatno: Readonly<Record<string, unknown>>

  constructor(
    status: number,
    key: string,
    fields: Record<string, string[]> = {},
    dodatno: Record<string, unknown> = {},
  ) {
    super(`${status} ${key}`)
    this.name = 'ApiError'
    this.status = status
    this.key = key
    this.fields = fields
    this.dodatno = dodatno
  }

  /** Zastavica iz ekstenzija; sve što nije poslano čita se kao "ne". */
  zastavica(ime: string): boolean {
    return this.dodatno[ime] === true
  }

  /** Prva greška za polje, ako je ima — obrasci prikazuju jednu po polju. */
  field(ime: string): string | undefined {
    return this.fields[ime]?.[0]
  }
}

/** `DispatchExpiryMin` → `dispatch_expiry_min`; `Prava[0].ServiceTypeId` → `prava`. */
export function uSnakeCase(svojstvo: string): string {
  const korijen = svojstvo.split(/[.[]/)[0] ?? svojstvo
  return korijen
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1_$2')
    .toLowerCase()
}

export function problemUGresku(status: number, problem: ProblemDocument | null): ApiError {
  const polja: Record<string, string[]> = {}

  for (const [svojstvo, kodovi] of Object.entries(problem?.errors ?? {})) {
    const ime = uSnakeCase(svojstvo)
    polja[ime] = [...(polja[ime] ?? []), ...kodovi]
  }

  const dodatno: Record<string, unknown> = {}

  for (const [clan, vrijednost] of Object.entries(problem ?? {})) {
    if (!STANDARDNI.includes(clan)) dodatno[clan] = vrijednost
  }

  return new ApiError(status, problem?.title || NEPOZNATA_GRESKA, polja, dodatno)
}
